import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);

  const navigation = [
    { name: 'Home', href: '/' },
    {
      name: 'Services',
      href: '/services',
      dropdown: [
        { name: 'All Services', href: '/services' },
        { name: 'AI Solutions', href: '/ai-solutions' },
        { name: 'Medical Devices', href: '/services/medical-devices' },
        { name: 'Software', href: '/services/software' },
        { name: 'AI Integration', href: '/services/ai' },
      ],
    },
    { name: 'Sketch 2 Scale', href: '/sketch2scale' },
    { name: 'Portfolio', href: '/portfolio' },
    { name: 'About', href: '/about' },
    { name: 'Blog', href: '/blog' },
    { name: 'Founder Toolkit', href: '/founder-toolkit' },
    { name: 'Contact', href: '/contact' },
  ];

  const isActive = (path) => {
    if (path === '/services') {
      return (
        location.pathname === '/services' ||
        location.pathname.startsWith('/services/') ||
        location.pathname.startsWith('/ai-solutions')
      );
    }
    return location.pathname === path;
  };

  const isDropdownItemActive = (path) => {
    if (path === '/services') {
      return location.pathname === '/services' && !location.pathname.includes('/services/');
    }
    return (
      location.pathname === path ||
      (path === '/services/medical-devices' && location.pathname.includes('/services#medical-devices')) ||
      (path === '/services/software' && location.pathname.includes('/services#software')) ||
      (path === '/services/ai' && location.pathname.includes('/services#ai'))
    );
  };

  // Handle clicks outside the dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsServicesOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Track scroll for subtle background shift
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Lock body scroll when mobile drawer is open
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  return (
    <nav
      className={`fixed top-0 w-full z-50 transition-all duration-500 border-b ${
        scrolled
          ? 'bg-[#0B1121]/80 border-white/[0.08] shadow-lg shadow-black/20'
          : 'bg-[#0B1121]/60 border-white/[0.06]'
      } backdrop-blur-2xl`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo - left */}
          <Link to="/" className="flex items-center shrink-0">
            <img
              src="/images/p2p-logo.png"
              alt="P2P Digital"
              width={500}
              height={500}
              className="h-8 w-8 object-contain object-left"
            />
          </Link>

          {/* Desktop nav links - center */}
          <div className="hidden lg:flex items-center gap-1">
            {navigation.map((item) => (
              <div key={item.name} className="relative" ref={item.dropdown ? dropdownRef : null}>
                {item.dropdown ? (
                  <>
                    <button
                      onClick={() => setIsServicesOpen(!isServicesOpen)}
                      onMouseEnter={() => setIsServicesOpen(true)}
                      className={`inline-flex items-center gap-1 px-3 py-2 text-sm font-sans font-medium rounded-lg transition-all duration-200 ${
                        isActive(item.href)
                          ? 'text-white'
                          : 'text-slate-300 hover:text-white'
                      }`}
                    >
                      {item.name}
                      <ChevronDown
                        className={`h-3.5 w-3.5 transition-transform duration-300 ${
                          isServicesOpen ? 'rotate-180' : ''
                        }`}
                      />
                      {isActive(item.href) && (
                        <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-cyan-500 rounded-full" />
                      )}
                    </button>

                    {/* Dropdown */}
                    <div
                      className={`absolute left-1/2 -translate-x-1/2 pt-2 transition-all duration-200 ${
                        isServicesOpen
                          ? 'opacity-100 translate-y-0 pointer-events-auto'
                          : 'opacity-0 -translate-y-2 pointer-events-none'
                      }`}
                      onMouseLeave={() => setIsServicesOpen(false)}
                    >
                      <div className="w-56 bg-[#1E293B]/95 backdrop-blur-xl rounded-xl border border-white/[0.06] shadow-lg shadow-black/30 py-1.5 overflow-hidden">
                        {item.dropdown.map((dropdownItem) => (
                          <Link
                            key={dropdownItem.name}
                            to={dropdownItem.href}
                            className={`block px-4 py-2.5 text-sm font-sans transition-all duration-150 ${
                              isDropdownItemActive(dropdownItem.href)
                                ? 'text-cyan-400 bg-white/[0.06]'
                                : 'text-slate-300 hover:text-white hover:bg-white/[0.04]'
                            }`}
                            onClick={() => setIsServicesOpen(false)}
                          >
                            {dropdownItem.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <Link
                    to={item.href}
                    className={`relative px-3 py-2 text-sm font-sans font-medium rounded-lg transition-all duration-200 ${
                      isActive(item.href)
                        ? 'text-white'
                        : 'text-slate-300 hover:text-white'
                    }`}
                  >
                    {item.name}
                    {isActive(item.href) && (
                      <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-cyan-500 rounded-full" />
                    )}
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* CTA - right (desktop) */}
          <div className="hidden lg:block shrink-0">
            <Link
              to="/contact"
              className="inline-flex items-center px-6 py-2.5 rounded-xl font-semibold text-sm text-white transition-all duration-300 hover:scale-[1.03] active:scale-[0.98]"
              style={{
                background: 'linear-gradient(135deg, #0ea5e9, #06b6d4)',
                boxShadow: '0 0 20px rgba(6, 182, 212, 0.3), 0 2px 8px rgba(0,0,0,0.3)',
              }}
            >
              Get Started
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden inline-flex items-center justify-center p-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/[0.06] transition-colors duration-200"
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`lg:hidden fixed inset-0 top-16 z-40 transition-all duration-300 ${
          isOpen
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/50"
          onClick={() => setIsOpen(false)}
        />

        {/* Drawer panel */}
        <div
          className={`relative bg-[#111827]/95 backdrop-blur-2xl border-r border-white/[0.06] h-full w-72 max-w-[85vw] overflow-y-auto transition-transform duration-300 ${
            isOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="px-4 py-6 space-y-1">
            {navigation.map((item) => (
              <div key={item.name}>
                {item.dropdown ? (
                  <>
                    <button
                      onClick={() => setIsServicesOpen(!isServicesOpen)}
                      className={`flex items-center justify-between w-full px-3 py-3 rounded-lg text-base font-sans font-medium transition-all duration-200 ${
                        isActive(item.href)
                          ? 'text-white bg-white/[0.06]'
                          : 'text-slate-300 hover:text-white hover:bg-white/[0.04]'
                      }`}
                    >
                      {item.name}
                      <ChevronDown
                        className={`h-4 w-4 transition-transform duration-300 ${
                          isServicesOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </button>

                    <div
                      className={`overflow-hidden transition-all duration-300 ${
                        isServicesOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                      }`}
                    >
                      <div className="pl-3 mt-1 space-y-0.5 border-l border-white/[0.06] ml-3">
                        {item.dropdown.map((dropdownItem) => (
                          <Link
                            key={dropdownItem.name}
                            to={dropdownItem.href}
                            className={`block px-3 py-2.5 rounded-lg text-sm font-sans transition-all duration-150 ${
                              isDropdownItemActive(dropdownItem.href)
                                ? 'text-cyan-400 bg-white/[0.06]'
                                : 'text-slate-400 hover:text-white hover:bg-white/[0.04]'
                            }`}
                            onClick={() => {
                              setIsOpen(false);
                              setIsServicesOpen(false);
                            }}
                          >
                            {dropdownItem.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <Link
                    to={item.href}
                    className={`block px-3 py-3 rounded-lg text-base font-sans font-medium transition-all duration-200 ${
                      isActive(item.href)
                        ? 'text-white bg-white/[0.06]'
                        : 'text-slate-300 hover:text-white hover:bg-white/[0.04]'
                    }`}
                    onClick={() => setIsOpen(false)}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}

            {/* Mobile CTA */}
            <div className="pt-4 mt-4 border-t border-white/[0.06]">
              <Link
                to="/contact"
                className="flex items-center justify-center w-full px-6 py-3 rounded-xl font-semibold text-sm text-white transition-all duration-300 active:scale-[0.98]"
                style={{
                  background: 'linear-gradient(135deg, #0ea5e9, #06b6d4)',
                  boxShadow: '0 0 20px rgba(6, 182, 212, 0.3), 0 2px 8px rgba(0,0,0,0.3)',
                }}
                onClick={() => setIsOpen(false)}
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
